import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { Ico } from "../../../components/icons/icons.jsx";
import { AssetModalField } from "../../Accounts/components/AssetModalField.jsx";

export function AssignDeviceAccountModal({ open, onClose, onSave, device, accounts = [], saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [accountId, setAccountId] = useState("");
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (!open) return;
    setAccountId("");
    setError(null);
  }, [open, device?.id]);

  if (!open || !device) return null;

  const options = accounts.map((account) => ({ value: account.id, label: account.companyName }));

  const handleSubmit = async () => {
    if (!accountId) {
      setError("Select an account");
      return;
    }
    setError(null);
    try {
      await onSave(accountId);
      onClose();
    } catch (err) {
      setError(err.message ?? "Failed to assign device");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="assign-device-account-title" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(480px, 96vw)", zIndex: 71, background: ap.cardGradient, border: `1px solid ${ap.cardBorder}`, borderRadius: 14, boxShadow: ap.cardShadowLg, overflow: "hidden" }}>
        <ModalShellHeader title="Assign to account" titleId="assign-device-account-title" subtitle={device.deviceId} icon={Ico.building} onClose={onClose} closeLabel="Close assign form" dark={dark} c={c} A={A} ap={ap} />
        <div style={{ padding: "16px 20px 20px" }}>
          <AssetModalField label="Account" value={accountId} onChange={setAccountId} placeholder="Select account" error={error} icon="building" as="select" options={options} />
          {accounts.length === 0 && (
            <p style={{ margin: "0 0 12px", fontSize: 12, color: c.sub }}>No accounts available.</p>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={saving} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Assigning…" : "Assign device"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function AssignExistingDeviceModal({ open, onClose, onSave, accountName, devices = [], saving = false, getDeviceTypeName }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [deviceId, setDeviceId] = useState("");
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (!open) return;
    setDeviceId("");
    setError(null);
  }, [open]);

  if (!open) return null;

  const options = devices.map((device) => {
    const typeName = getDeviceTypeName?.(device.deviceTypeId) ?? device.deviceType;
    const label = typeName ? `${device.deviceId} · ${typeName}` : device.deviceId;
    return { value: device.id, label };
  });

  const handleSubmit = async () => {
    if (!deviceId) {
      setError("Select a device");
      return;
    }
    setError(null);
    try {
      await onSave(deviceId);
      onClose();
    } catch (err) {
      setError(err.message ?? "Failed to assign device");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="assign-existing-device-title" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(480px, 96vw)", zIndex: 71, background: ap.cardGradient, border: `1px solid ${ap.cardBorder}`, borderRadius: 14, boxShadow: ap.cardShadowLg, overflow: "hidden" }}>
        <ModalShellHeader title="Assign existing device" titleId="assign-existing-device-title" subtitle={`Add an unassigned device to ${accountName}`} icon={Ico.device} onClose={onClose} closeLabel="Close assign form" dark={dark} c={c} A={A} ap={ap} />
        <div style={{ padding: "16px 20px 20px" }}>
          <AssetModalField label="Device" value={deviceId} onChange={setDeviceId} placeholder="Select device" error={error} icon="device" as="select" options={options} />
          {devices.length === 0 && (
            <p style={{ margin: "0 0 12px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>No unassigned devices in the pool. Create a device on the Devices page first.</p>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={saving || devices.length === 0} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving || devices.length === 0 ? 0.7 : 1 }}>
              {saving ? "Assigning…" : "Assign device"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
